import React from 'react'
import { Form } from 'react-bootstrap'
import { SumInsuredOption } from '../types'

interface Props {
  options: SumInsuredOption[]
  selectedSICode: string | null
  computedSI: number
  onSelect: (siCode: string) => void
  showOnlyValue?: boolean
}

const formatCurrency = (amount: number): string => {
  return '₹' + amount.toLocaleString('en-IN')
}

const SumInsuredSelector: React.FC<Props> = ({ options, selectedSICode, computedSI, onSelect, showOnlyValue }) => {
  if (options.length === 0) return null

  // Single option → auto-selected, show compact info
  if (options.length === 1) {
    const opt = options[0]
    const displayVal =
      opt.type === 'CtcMultiplier'
        ? `${formatCurrency(computedSI)} (${opt.multiplier}× CTC)`
        : formatCurrency(opt.fixedAmount ?? 0)
    // Only show label if it adds info beyond the amount
    const labelDiffers =
      opt.displayLabel &&
      opt.displayLabel !== formatCurrency(opt.fixedAmount ?? 0) &&
      opt.displayLabel !== formatCurrency(computedSI)
    return (
      <div className='mb-6'>
        <label className='form-label fw-bolder fs-6'>Sum Insured</label>
        <div className='border border-primary bg-light-primary rounded p-3'>
          <div className='fw-bolder fs-6'>{displayVal}</div>
          {labelDiffers && (
            <div className='text-muted fs-7 mt-1'>{opt.displayLabel}</div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className='mb-6'>
      <label className='form-label fw-bolder fs-6'>Select Sum Insured</label>
      <div className='row g-3'>
        {options.map((opt) => {
          const isSelected = opt.optionCode === selectedSICode
          const displayAmount =
            opt.type === 'CtcMultiplier' && opt.optionCode === selectedSICode
              ? formatCurrency(computedSI)
              : opt.type === 'Fixed'
              ? formatCurrency(opt.fixedAmount ?? 0)
              : opt.displayLabel

          return (
            <div className='col-6 col-md-4' key={opt.optionCode}>
              <div
                className={`border rounded p-3 cursor-pointer ${
                  isSelected
                    ? 'border-primary bg-light-primary'
                    : 'border-gray-300 bg-white hover-elevate-up'
                }`}
                onClick={() => onSelect(opt.optionCode)}
                role='button'
              >
                <div className='d-flex align-items-center'>
                  <Form.Check
                    type='radio'
                    name='si-select'
                    checked={isSelected}
                    onChange={() => onSelect(opt.optionCode)}
                    className='me-3'
                  />
                  <div>
                    <div className='fw-bolder fs-6'>{displayAmount}</div>
                    {opt.displayLabel && opt.displayLabel !== displayAmount && (
                      <div className='text-muted fs-7 mt-1'>{opt.displayLabel}</div>
                    )}
                    {opt.type === 'CtcMultiplier' && (
                      <div className='text-muted fs-8'>
                        {opt.multiplier}× CTC
                        {opt.minCap != null && ` | Min: ${formatCurrency(opt.minCap)}`}
                        {opt.maxCap != null && ` | Max: ${formatCurrency(opt.maxCap)}`}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default SumInsuredSelector
